import React, { useMemo, useState, useEffect } from "react";
import dayjs from "dayjs";
import PopupReaction from "../popup/popupReaction";
import isBetween from "dayjs/plugin/isBetween";
import '../../styles/customScroll.css';
import { httpss } from "../../utils/host";
dayjs.extend(isBetween);

const ReportDay = ({ selectedPageId }) => {
  // console.log("data", selectedPageId);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [startDate, setStartDate] = useState(dayjs().startOf("day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().endOf("day").format("YYYY-MM-DD"));
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); 
  const [data, setData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  const fetDataPage = async () => {
    if (!selectedPageId) return null;
    fetch(`${httpss}/apis_v1/user/pages_detail?pageId=${selectedPageId}`)
    .then(res => res.json())
    .then(res => {
      if (Array.isArray(res)) {
        setData(res[0]); 
      }
    })
    .catch(err => console.error('Error loading selected page data:', err));
  }
  
  useEffect(() => {
    fetDataPage();
    const intervalId = setInterval(fetDataPage, 10000);
    return () => clearInterval(intervalId);
  }, [selectedPageId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); 

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const topUsers = useMemo(() => {
    const start = dayjs(startDate);
    const end = dayjs(endDate).endOf("day");
    const userActivityCount = {};
  
    // 1. Reaction
    if (filterType !== "comment") {
      data?.PostFBs?.forEach((post) => {
        post?.ReactionFBs?.forEach((reaction) => {
          const reactionTime = reaction.created_time || reaction.createdAt;
          if (reactionTime && dayjs(reactionTime).isBetween(start, end)) {
            const userId = reaction.user?.UserId;
            if (!userActivityCount[userId]) {
              userActivityCount[userId] = {
                count: 0,
                user: reaction.user,
                user365: reaction.user365,
                interactions: {
                  reaction: 0,
                  comment: 0
                }
              };
            }
            userActivityCount[userId].count += 1;
            userActivityCount[userId].interactions.reaction += 1;
          }
        });
      });
    }
  
    // 2. Comment
    if (filterType !== "reaction") {
      data?.PostFBs?.forEach((post) => {
        post?.CommentFBs?.forEach((comment) => {
          const commentTime = comment.created_time || comment.createdAt;
          if (commentTime && dayjs(commentTime).isBetween(start, end)) {
            const userId = comment.user?.UserId || comment.UserFBId;
            if (!userActivityCount[userId]) {
              userActivityCount[userId] = {
                count: 0,
                user: comment.user,
                user365: comment.user365,
                interactions: {
                  reaction: 0,
                  comment: 0
                }
              };
            }
            userActivityCount[userId].count += 1;
            userActivityCount[userId].interactions.comment += 1;
          }
        });
      });
    }
  
    let sorted = Object.values(userActivityCount).sort((a, b) => b.count - a.count);
  
    if (debouncedSearchTerm) {
      sorted = sorted.filter((u) =>
        u.user?.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
    }
  
    return sorted;
  }, [data, startDate, endDate, debouncedSearchTerm, filterType]);
  

  const handleShowPosts = (userObj) => {
    const userId = userObj.user?.UserId || userObj.user?.id;
    const start = dayjs(startDate);
    const end = dayjs(endDate).endOf("day");
  
    const interactedPosts = data?.PostFBs?.map((post) => {
      let interactionType = [];
      let userComment = null;
      let reactionType = null;
  
      // Tìm reaction của user (nếu có)
      const userReaction = post.ReactionFBs?.find((reaction) => {
        const reactionTime = reaction.created_time || reaction.createdAt;
        const rUserId = reaction.user?.UserId;
        return (
          rUserId === userId &&
          dayjs(reactionTime).isAfter(start) &&
          dayjs(reactionTime).isBefore(end)
        );
      });
  
      if (userReaction) {
        interactionType.push("Reaction");
        reactionType = userReaction.type; // Đây sẽ là: LIKE, LOVE, ANGRY, SAD, CARE, HAHA, WOW...
      }
  
      // Tìm comment của user (nếu có)
      const comment = post.CommentFBs?.find((comment) => {
        const commentTime = comment.created_time || comment.createdAt;
        const cUserId = comment.user?.UserId || comment.UserFBId;
        return (
          cUserId === userId &&
          dayjs(commentTime).isAfter(start) &&
          dayjs(commentTime).isBefore(end)
        );
      });
      if (comment) {
        interactionType.push("Comment");
        userComment = comment.message;
      }
  
      if (interactionType.length > 0) {
        return {
          ...post,
          interactionType: interactionType.join(" & "),
          userComment,
          reactionType, // like/love/angry...
        };
      }
      return null;
    }).filter(Boolean);
  
    setSelectedUser({
      name: userObj.user?.name,
      UserId: userObj.user?.UserId,
      user365: userObj.user?.User365Name,
      interactedPosts,
    });
  
    setShowPopup(true);
  };
  
  const closePopup = () => {
    setShowPopup(false);
    setSelectedUser(null);
  };

  const paginatedUsers = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    const endIdx = startIdx + itemsPerPage;
    return topUsers.slice(startIdx, endIdx);
  }, [topUsers, currentPage, itemsPerPage]);
  
  const totalPages = useMemo(() => {
    return Math.ceil(topUsers.length / itemsPerPage) || 1;
  }, [topUsers, itemsPerPage]);
  
  // Function to determine what to display in the interaction type column
  const getInteractionType = (user) => {
    if (filterType === "reaction") return "Reaction";
    if (filterType === "comment") return "Comment";
    
    // For "all" filter type, show the specific interaction types
    const { reaction = 0, comment = 0 } = user.interactions || {};
    
    if (reaction > 0 && comment > 0) return "Reaction & Comment";
    if (reaction > 0) return "Reaction";
    if (comment > 0) return "Comment";
    return "-";
  };

  return (
    <div className="border rounded-xl shadow p-2 bg-white">
      <div className="flex gap-2 items-end">
        <p className="text-xl font-semibold text-blue-600 uppercase">Tương tác ngày</p>
        <p>Tìm thấy: {topUsers.length}</p>
      </div>

      {/* Bộ lọc */}
      <div className="flex flex-wrap gap-4 items-center text-xs">
        <div>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border p-1 rounded outline-none"
          />
        </div>
        <div>
          <label className="mr-2">Đến</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border p-1 rounded outline-none"
          />
        </div>
        <div>
          <input
            type="search"
            placeholder="Tìm kiếm..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border p-1 rounded outline-none"
          />
        </div>
        <div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border p-1 rounded outline-none"
          >
            <option value="all">Tất cả</option>
            <option value="reaction">Chỉ reaction</option>
            <option value="comment">Chỉ comment</option>
          </select>
        </div>

      </div>

      {/* Bảng dữ liệu */}
      <div className="mt-2">
        {topUsers.length > 0 ? (
          <div className="max-h-[300px] overflow-x-auto mt-2 rounded-lg custom-scrollbar">
            <table className="min-w-full table-auto border border-gray-300 rounded-lg">
              <thead className="sticky top-0 bg-gray-200 z-0">
                <tr className="text-sm text-left">
                  <th className="border p-2">STT</th>
                  <th className="border p-2">User</th>
                  <th className="border p-2">User365</th>
                  <th className="border p-2">Loại tương tác</th>
                  <th className="border p-2">Số lượng</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user, index) => (
                  <tr key={index} className="hover:bg-gray-50 text-sm">
                    <td className="border p-2">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td
                      className="border p-2 text-blue-600 hover:underline cursor-pointer"
                      onClick={() => handleShowPosts(user)}
                    >
                      {user.user?.name}
                    </td>
                    <td className="border p-2">
                      {user.user?.User365Name || "_"}
                    </td>
                    <td className="border p-2">
                      {getInteractionType(user)}
                    </td>
                    <td className="border p-2">{user.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500 mt-2">Không có dữ liệu phù hợp.</p>
        )}
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-2 text-xs">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-2 py-1 border rounded disabled:opacity-50"
          >
            &lt;
          </button>
          <span>
            Trang {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-2 py-1 border rounded disabled:opacity-50"
          >
            &gt;
          </button>
        </div>

        <div className="flex items-center gap-2">
          <label>Số dòng / trang:</label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1); // reset về trang đầu
            }}
            className="border rounded p-1"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={1000}>1000</option>
          </select>
        </div>
      </div>

      {/* Popup */}
      {showPopup && selectedUser && (
        <PopupReaction
          isOpen={showPopup}
          onClose={closePopup}
          selectedUser={selectedUser}
          refresh={fetDataPage}
        />
      )}
    </div>
  );
};

export default ReportDay;